/**
 * Team → Overview.
 *
 * The goals scoreboard (is the machine on pace), the triage panel (what
 * needs me now), then one card per AI employee: what state it is in, what it
 * is working on, what is queued behind that, and whether it is waiting on an
 * answer. The state word is the server's (`system/services/ai_team.py`), so
 * this page and the Slack alerts can never disagree.
 *
 * Freshness comes from two directions: a 10s poll (the state words depend on
 * elapsed time, not only on row changes) plus an SSE subscription per
 * employee queue, so a job landing shows up immediately.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { adminApi, type AiTeamAgent } from "../../api/admin";
import { LiveIndicator } from "../../components/LiveIndicator";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { agentStateTone } from "../../lib/aiTeam";
import { formatRelative } from "../../lib/formatRelative";
import { useLiveUpdates } from "../../lib/hooks/useLiveUpdates";
import { AgentCreateModal } from "./AgentCreateModal";
import { EmployeeDrawer } from "./EmployeeDrawer";
import { GoalsScoreboard } from "./GoalsScoreboard";
import { TriagePanel } from "./TriagePanel";

export function TeamOverviewPage() {
  const qc = useQueryClient();
  const agentsQuery = adminApi.aiTeam.agents();
  // The drawer is URL-backed (`?agent=…&tab=…&job=…`) so questions, jobs and
  // employees are linkable from anywhere — and back closes what it opened.
  const [searchParams, setSearchParams] = useSearchParams();
  const openAgentId = searchParams.get("agent");
  const openAgent = (id: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("agent", id);
      return p;
    });
  };
  const closeDrawer = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("agent");
      p.delete("tab");
      p.delete("job");
      return p;
    });
  };
  const [hireOpen, setHireOpen] = useState(false);

  const agents = agentsQuery.data;
  const topics = useMemo(
    () =>
      (agents ?? []).flatMap((a) => [
        `jobs:agent:${a.id}`,
        `questions:agent:${a.id}`,
      ]),
    [agents],
  );
  // Trailing-debounced whole-subtree invalidate. The trigger notifies on
  // every job transition (since migration 7c13f5571807), so a claim+complete
  // burst would otherwise refetch the ~6 queries under ["admin","ai-team"]
  // several times per second. Debounce over key-narrowing: the subtree is
  // small (agents, agent, agent-jobs, questions, attention) and narrowing
  // risks missing the cross-cutting counts every card shows.
  const debounceRef = useRef<number | null>(null);
  const onLiveEvent = useCallback(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      qc.invalidateQueries({ queryKey: ["admin", "ai-team"] });
    }, 500);
  }, [qc]);
  const { status: liveStatus } = useLiveUpdates(topics, onLiveEvent);

  return (
    <>
      <PageHeader
        title="AI Team"
        description="The AI employees, their queues and what they are blocked on. Suspend, inspect jobs and answer questions from the employee drawer."
        actions={
          <>
            <LiveIndicator status={liveStatus} />
            <button
              type="button"
              className="btn btn-main"
              onClick={() => setHireOpen(true)}
            >
              Hire employee
            </button>
          </>
        }
      />

      <GoalsScoreboard />

      <TriagePanel />

      <p className="text-xs text-ink-low -mt-3 mb-5">
        <Link className="link" to="/tasks">
          Tasks the AI team needs from you live under Tasks →
        </Link>
      </p>

      <QueryBoundary
        query={agentsQuery}
        empty={
          <div className="empty-state">
            <div className="title">No AI employees yet</div>
            <p className="hint">
              "Hire employee" provisions the principal, grants its role and
              mints its first token.
            </p>
          </div>
        }
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <section
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            }}
          >
            {rows.map((agent) => (
              <EmployeeCard
                key={agent.id}
                agent={agent}
                onOpen={() => openAgent(agent.id)}
              />
            ))}
          </section>
        )}
      </QueryBoundary>

      <AgentCreateModal
        open={hireOpen}
        onClose={() => setHireOpen(false)}
        onCreated={(agentId) => openAgent(agentId)}
      />

      <EmployeeDrawer agentId={openAgentId} onClose={closeDrawer} />
    </>
  );
}

function EmployeeCard({
  agent,
  onOpen,
}: {
  agent: AiTeamAgent;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="panel panel--hoverable text-left"
      onClick={onOpen}
      style={{ display: "block", width: "100%", cursor: "pointer" }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-ink truncate">
            {agent.name || agent.agent_slug || "(unnamed)"}
          </div>
          <div className="font-mono text-xs text-ink-low truncate">
            {agent.agent_slug ?? agent.email}
          </div>
        </div>
        <Pill value={agent.state} tone={agentStateTone(agent.state)} />
      </div>

      <div className="text-sm text-ink-medium mb-3" style={{ minHeight: "2.5rem" }}>
        {agent.current_job ? (
          <>
            <span className="font-mono text-xs">{agent.current_job.name}</span>
            <span className="text-ink-low text-xs">
              {" · started "}
              {formatRelative(agent.current_job.started_at)}
            </span>
          </>
        ) : (
          <span className="text-ink-low">No job in flight</span>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm mb-3">
        <Count label="submitted" value={agent.submitted_count} />
        <Count label="working" value={agent.working_count} />
        <Count label="parked" value={agent.parked_count} />
        {agent.open_questions > 0 ? (
          <span className="ml-auto">
            <Pill
              value={`${agent.open_questions} open q`}
              tone="signal"
            />
          </span>
        ) : null}
      </div>

      <div className="text-xs text-ink-low flex items-center gap-3 pt-3 border-t border-rule">
        <span>seen {formatRelative(agent.last_seen_at)}</span>
        {agent.stream_connected !== undefined ? (
          <span>receiver {agent.stream_connected ? "connected" : "not connected"}</span>
        ) : null}
        <span>digest {formatRelative(agent.last_digest_at)}</span>
      </div>
    </button>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <span className="flex items-baseline gap-1">
      <span className="tnum font-medium text-ink">{value}</span>
      <span className="text-xs text-ink-low">{label}</span>
    </span>
  );
}
